import { TFunction } from 'react-i18next';

import { MediaElement } from 'editor/src/store/design/types';
import { WarningLevel } from 'editor/src/store/editorModules/warnings/types';
import { GalleryImage, ImageState, MimeType } from 'editor/src/store/gallery/types';
import { DpiLevels } from 'editor/src/store/hostSettings/types';
import { DPI_ADJUSTED } from 'editor/src/store/plugins/utils/imageManipulationUtils';

/**
 * Presentation helpers for the DPI indicator, shared by the editor's own warning
 * surfaces and by the product personalizer. They live here rather than next to a
 * specific menu button so neither side has to reach into the other's UI tree.
 */

export function canShowGoodDPI(galleryImage: GalleryImage | undefined) {
  // Only reassure the customer once the asset is actually uploaded. While it is
  // still uploading the DPI is unknown, and getDPIWarningLabel would render a
  // misleading green "good dpi" for an image that may well turn out low-res.
  return galleryImage?.type !== MimeType.Unknown && galleryImage?.state === ImageState.UPLOADED;
}

export function getDPIWarningTooltip(
  dpi: number | undefined,
  level: WarningLevel | undefined,
  t: TFunction<'translation', undefined>,
  imageAutoEnhance?: boolean,
  adjusted?: boolean,
) {
  if (imageAutoEnhance && level) {
    return t('low-dpi-tooltip');
  }
  if (adjusted && level) {
    return t('Image was adjusted');
  }
  let levelLabel;
  switch (level) {
    case WarningLevel.High:
      levelLabel = t('editor-low');
      break;
    case WarningLevel.Medium:
      levelLabel = t('editor-medium');
      break;
    default:
      levelLabel = t('editor-good');
      break;
  }
  const dpiValue = dpi ? `${Math.round(dpi)} ` : '';
  return `${levelLabel} ${dpiValue}${t('editor-dpi')}`;
}

export function getDPIWarningLabel(dpi: number | undefined, t: TFunction<'translation', undefined>) {
  const dpiValue = dpi ? Math.round(dpi) : t('editor-good');
  return `${dpiValue} ${t('editor-dpi')}`;
}

// The shopper-facing wording: plain words plus the numbers that say how far off the image is.
// The minimum is the threshold this level falls under, so a medium image is never told it is
// below a number it already passes.
export function getLowQualityMessage(
  dpi: number | undefined,
  level: WarningLevel,
  limits: DpiLevels,
  t: TFunction<'translation', undefined>,
) {
  if (!dpi) {
    return t('Low quality image message');
  }
  const min = level === WarningLevel.High ? limits.low : limits.medium;
  return t('editor-low-quality-dpi-message', { dpi: Math.round(dpi), min: Math.round(min) });
}

export function isImageAdjusted(element: MediaElement | undefined) {
  return element?.type === 'image' && element.adjustments?.some(({ name }) => name === DPI_ADJUSTED);
}
